import React, { useState, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProgressBar from '../components/Shared/ProgressBar';
import ConfirmModal from '../components/Shared/ConfirmModal';
import { format, parse, isAfter, setYear, getYear, parseISO, isValid } from 'date-fns';

const Dashboard = () => {
  const {
    clients,
    plans,
    tags,
    agencies,
    tasks,
    getClientUsedHours,
    addClient,
    removeClient,
    isLoading,
    syncError,
    refreshData
  } = useAppContext();
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [showWithOpenTasks, setShowWithOpenTasks] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
  const [newClient, setNewClient] = useState({
    name: '',
    website: '',
    notes: '',
    domain: '',
    planId: '',
    agencyId: '',
    tags: [],
    resetDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [selectedClients, setSelectedClients] = useState([]);
  const fileInputRef = useRef(null);

  const formatRenewalDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const slashDate = parse(dateStr, 'dd/MM/yyyy', new Date());
    if (isValid(slashDate)) return format(slashDate, 'MMM d, yyyy');
    const oldSlashDate = parse(dateStr, 'MM/dd/yyyy', new Date());
    if (isValid(oldSlashDate)) return format(oldSlashDate, 'MMM d, yyyy');
    const isoDate = parseISO(dateStr);
    if (isValid(isoDate) && dateStr.includes('-') && dateStr.split('-')[0].length === 4) {
      return format(isoDate, 'MMM d, yyyy');
    }
    try {
      const today = new Date();
      const currentYear = getYear(today);
      const parsedCycle = parse(dateStr, 'MM-dd', new Date());
      if (isValid(parsedCycle)) {
        let nextOccurrence = setYear(parsedCycle, currentYear);
        if (isAfter(today, nextOccurrence)) nextOccurrence = setYear(nextOccurrence, currentYear + 1);
        return format(nextOccurrence, 'MMM d, yyyy');
      }
    } catch (e) {}
    return dateStr;
  };

  const getRenewalSortTime = (dateStr) => {
    if (!dateStr) return Number.MAX_SAFE_INTEGER;

    const slashDate = parse(dateStr, 'dd/MM/yyyy', new Date());
    if (isValid(slashDate)) return slashDate.getTime();

    const oldSlashDate = parse(dateStr, 'MM/dd/yyyy', new Date());
    if (isValid(oldSlashDate)) return oldSlashDate.getTime();

    const isoDate = parseISO(dateStr);
    if (isValid(isoDate) && dateStr.includes('-') && dateStr.split('-')[0].length === 4) {
      return isoDate.getTime();
    }

    const recurringDate = parse(dateStr, 'MM-dd', new Date());
    if (isValid(recurringDate)) {
      const today = new Date();
      let nextOccurrence = setYear(recurringDate, getYear(today));
      if (isAfter(today, nextOccurrence)) nextOccurrence = setYear(nextOccurrence, getYear(today) + 1);
      return nextOccurrence.getTime();
    }

    return Number.MAX_SAFE_INTEGER;
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = (client.name || '').toLowerCase().includes(search.toLowerCase());
    const matchesPlan = planFilter === '' || client.planId === planFilter;
    const openTasksCount = tasks.filter(t => t.clientId === client.id && t.status === 'Open').length;
    const matchesOpenTasks = showWithOpenTasks ? openTasksCount > 0 : true;
    return matchesSearch && matchesPlan && matchesOpenTasks;
  }).sort((a, b) => {
    if (sortBy === 'renewal') {
      return getRenewalSortTime(a.resetDate) - getRenewalSortTime(b.resetDate);
    }
    return (a.name || '').localeCompare(b.name || '');
  });

  const toggleSelection = (id) => {
    setSelectedClients(prev =>
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const dateParts = newClient.resetDate.split('-');
    const formattedReset = dateParts.length === 3 ? `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}` : format(new Date(), 'dd/MM/yyyy');
    addClient({ ...newClient, resetDate: formattedReset });
    setIsModalOpen(false);
    setNewClient({ name: '', website: '', notes: '', domain: '', planId: '', agencyId: '', tags: [], resetDate: format(new Date(), 'yyyy-MM-dd') });
  };

  const getFaviconUrl = (url) => {
    if (!url) return null;
    try {
      const hostname = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
      return `https://www.google.com/s2/favicons?domain=${hostname}&sz=128`;
    } catch (e) {
      return `https://www.google.com/s2/favicons?domain=${url}&sz=128`;
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8 pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 sm:gap-4">
        <div>
          <p className="text-teal-500 font-bold text-sm uppercase tracking-widest mb-1">Portfolio Management</p>
          <h1 className="text-3xl sm:text-4xl font-black text-white">Shift Creative Portal</h1>
        </div>
        <div className="flex flex-wrap gap-3 items-center w-full md:w-auto">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-xs font-black uppercase tracking-wider ${
            syncError
              ? 'bg-red-500/10 border-red-500/30 text-red-400'
              : 'bg-teal-500/5 border-teal-500/20 text-teal-400'
          }`}>
            <span className={`w-2 h-2 rounded-full ${syncError ? 'bg-red-500 animate-pulse' : 'bg-teal-500'}`} />
            <span>{syncError ? 'Sync Error' : isLoading ? 'Syncing' : 'Synced'}</span>
            <button
              onClick={refreshData}
              disabled={isLoading}
              className="ml-1 text-slate-400 hover:text-white disabled:opacity-40 transition-colors"
              title="Refresh sync"
            >
              <SafeIcon icon={FiIcons.FiRefreshCw} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <input type="file" ref={fileInputRef} onChange={(e) => {}} className="hidden" accept=".csv" />
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex-1 sm:flex-none bg-teal-600 hover:bg-teal-500 text-white px-5 py-3 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-teal-900/20 font-bold text-sm"
            >
              <SafeIcon icon={FiIcons.FiPlus} /> Onboard
            </button>
            <button
              onClick={() => {}}
              className="p-3 bg-slate-900 border border-slate-800 text-slate-400 rounded-xl hover:text-white transition-all"
              title="Export CSV"
            >
              <SafeIcon icon={FiIcons.FiDownload} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-4 items-stretch xl:items-center">
        <div className="relative group flex-1 min-w-0">
          <SafeIcon icon={FiIcons.FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-teal-400" />
          <input
            type="text"
            placeholder="Search client accounts..."
            className="w-full bg-slate-900 border border-slate-800 rounded-2xl pl-12 pr-6 py-4 text-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all font-medium text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex bg-slate-900 p-1 rounded-2xl border border-slate-800">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-4 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 font-bold text-sm ${viewMode === 'grid' ? 'bg-teal-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              <SafeIcon icon={FiIcons.FiGrid} />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 font-bold text-sm ${viewMode === 'table' ? 'bg-teal-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              <SafeIcon icon={FiIcons.FiList} />
            </button>
          </div>

          <label className="flex items-center gap-2 text-slate-400 font-bold text-sm cursor-pointer hover:text-white transition-colors bg-slate-900 border border-slate-800 rounded-2xl px-4 py-3 select-none justify-center">
            <input
              type="checkbox"
              checked={showWithOpenTasks}
              onChange={(e) => setShowWithOpenTasks(e.target.checked)}
              className="sr-only"
            />
            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${showWithOpenTasks ? 'bg-teal-500 border-teal-500' : 'bg-slate-950 border-slate-700'}`}>
              {showWithOpenTasks && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-[10px]" />}
            </div>
            Open Tasks
          </label>

          <select
            className="w-40 bg-slate-900 border border-slate-800 rounded-2xl px-4 py-3 text-slate-400 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all appearance-none"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="">All Plans</option>
            {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>

          <select
            className="w-44 bg-slate-900 border border-slate-800 rounded-2xl px-4 py-3 text-slate-400 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all appearance-none"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort clients"
          >
            <option value="name">Sort: Name</option>
            <option value="renewal">Sort: Renewal Date</option>
          </select>
        </div>
      </div>

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map(client => {
            const plan = plans.find(p => p.id === client.planId) || {};
            const usedHours = getClientUsedHours(client.id);
            const isSelected = selectedClients.includes(client.id);
            const openTasksCount = tasks.filter(t => t.clientId === client.id && t.status === 'Open').length;

            return (
              <div key={client.id} className="relative group transition-all duration-300">
                <Link to={`/client/${client.id}`} className="block h-full">
                  <div className={`bg-slate-900 overflow-hidden rounded-3xl border transition-all h-full shadow-sm flex flex-col ${
                    isSelected ? 'border-teal-500 ring-1 ring-teal-500/50 shadow-teal-900/20' : 'border-slate-800 group-hover:bg-slate-800/50 group-hover:border-teal-900'
                  }`}>
                    <div className="p-6 pb-2 flex items-start gap-4">
                      <div
                        className={`mt-2 shrink-0 w-5 h-5 rounded border-2 cursor-pointer flex items-center justify-center transition-all ${
                          isSelected ? 'bg-teal-500 border-teal-500' : 'bg-slate-950 border-slate-700 hover:border-teal-400'
                        }`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelection(client.id); }}
                      >
                        {isSelected && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-xs" />}
                      </div>

                      <div className="relative shrink-0">
                        {openTasksCount > 0 && (
                          <div className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] font-black z-10 border-2 border-slate-900 shadow-md">
                            {openTasksCount}
                          </div>
                        )}
                        <div className="w-12 h-12 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center overflow-hidden shadow-lg">
                          {client.website ? (
                            <img
                              src={getFaviconUrl(client.website)}
                              alt={client.name}
                              className="w-8 h-8 object-contain"
                              onError={(e) => { e.target.parentElement.innerHTML = `<span class="text-lg font-black text-slate-500">${(client.name || '?')[0]}</span>`; }}
                            />
                          ) : (
                            <span className="text-lg font-black text-slate-500">{client.name ? client.name[0] : '?'}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0 pr-8">
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-1 line-clamp-1 group-hover:text-teal-400 uppercase transition-colors">{client.name || 'Unnamed Client'}</h3>
                        <div className="flex flex-wrap gap-1.5">
                          <span className="text-xs px-2 py-0.5 rounded font-black uppercase border" style={{
                            backgroundColor: plan.color ? `${plan.color}15` : '#33415515',
                            color: plan.color || '#64748b',
                            borderColor: plan.color ? `${plan.color}30` : '#47556930'
                          }}>
                            {plan.name || 'No Plan'}
                          </span>
                        </div>
                        {(client.domain || client.notes) && (
                          <div className="mt-3 space-y-1.5">
                            {client.domain && (
                              <p className="text-xs text-slate-400 line-clamp-1 font-medium normal-case flex items-center gap-1.5">
                                <SafeIcon icon={FiIcons.FiGlobe} className="shrink-0 text-slate-500" />
                                {client.domain}
                              </p>
                            )}
                            {client.notes && (
                              <p className="text-xs text-slate-500 line-clamp-2 font-medium normal-case flex items-start gap-1.5">
                                <SafeIcon icon={FiIcons.FiFileText} className="shrink-0 mt-0.5 text-slate-600" />
                                {client.notes}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-6 pt-4 mt-auto space-y-4">
                      <div className="flex justify-between text-xs sm:text-sm font-black uppercase">
                        <span className="text-slate-500">Utilization</span>
                        <span className="text-teal-400">{usedHours.toFixed(1)} / {plan.monthlyHours || 0}h</span>
                      </div>
                      <ProgressBar used={usedHours} total={plan.monthlyHours || 0} colorClass="bg-teal-500" />
                      <div className="text-xs sm:text-sm text-slate-600 font-bold uppercase flex items-center gap-1">
                        <SafeIcon icon={FiIcons.FiRefreshCw} /> Renewal: {formatRenewalDate(client.resetDate)}
                      </div>
                    </div>
                  </div>
                </Link>

                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteConfirm({ show: true, id: client.id }); }}
                  className="absolute top-4 right-4 p-2 bg-slate-950/50 backdrop-blur-md rounded-lg text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all border border-slate-800 z-20"
                >
                  <SafeIcon icon={FiIcons.FiTrash2} />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden shadow-2xl">
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full text-left min-w-[700px]">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-800/30">
                  <th className="px-6 py-5 w-10"></th>
                  <th className="px-6 py-5 text-sm font-black text-slate-500 uppercase tracking-widest">Client Name</th>
                  <th className="px-6 py-5 text-sm font-black text-slate-500 uppercase tracking-widest">Plan</th>
                  <th className="px-6 py-5 text-sm font-black text-slate-500 uppercase tracking-widest">Utilization</th>
                  <th className="px-6 py-5 text-sm font-black text-slate-500 uppercase tracking-widest">Renewal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filteredClients.map(client => {
                  const plan = plans.find(p => p.id === client.planId) || {};
                  const usedHours = getClientUsedHours(client.id);
                  const isSelected = selectedClients.includes(client.id);
                  const openTasksCount = tasks.filter(t => t.clientId === client.id && t.status === 'Open').length;

                  return (
                    <tr key={client.id} className={`hover:bg-slate-800/30 transition-colors group ${isSelected ? 'bg-teal-500/5' : ''}`}>
                      <td className="px-6 py-4">
                        <div
                          className={`w-5 h-5 rounded border-2 cursor-pointer flex items-center justify-center transition-all ${
                            isSelected ? 'bg-teal-500 border-teal-500' : 'bg-slate-950 border-slate-700'
                          }`}
                          onClick={() => toggleSelection(client.id)}
                        >
                          {isSelected && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-sm" />}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Link to={`/client/${client.id}`} className="block">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="font-bold text-slate-200 group-hover:text-teal-400 transition-colors text-sm uppercase">{client.name}</p>
                            {openTasksCount > 0 && (
                              <span className="bg-red-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full">
                                {openTasksCount}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 font-medium truncate max-w-[250px] mb-1">{client.website || 'No website'}</p>
                          {(client.domain || client.notes) && (
                            <div className="mt-2 space-y-1">
                              {client.domain && <p className="text-[10px] text-slate-400 truncate max-w-[250px]"><SafeIcon icon={FiIcons.FiGlobe} className="inline mr-1.5 text-slate-500" />{client.domain}</p>}
                              {client.notes && <p className="text-[10px] text-slate-500 truncate max-w-[250px]"><SafeIcon icon={FiIcons.FiFileText} className="inline mr-1.5 text-slate-600" />{client.notes}</p>}
                            </div>
                          )}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded text-xs font-black uppercase border" style={{
                          backgroundColor: plan.color ? `${plan.color}15` : '#33415515',
                          color: plan.color || '#64748b',
                          borderColor: plan.color ? `${plan.color}30` : '#47556930'
                        }}>
                          {plan.name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-32 space-y-1.5">
                          <div className="flex justify-between text-xs font-black uppercase">
                            <span className="text-slate-500">{usedHours.toFixed(1)}h</span>
                            <span className="text-teal-400">{plan.monthlyHours || 0}h</span>
                          </div>
                          <ProgressBar used={usedHours} total={plan.monthlyHours || 0} colorClass="bg-teal-500" />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono font-bold text-slate-500">{formatRenewalDate(client.resetDate)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}>
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-black text-white mb-6 sm:mb-8">New Client Registration</h2>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-black text-slate-500 uppercase tracking-widest mb-2 sm:mb-3">Organization Name</label>
                  <input autoFocus className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none transition-all text-sm" value={newClient.name} onChange={e => setNewClient({ ...newClient, name: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-sm font-black text-slate-500 uppercase tracking-widest mb-2 sm:mb-3">Website URL</label>
                  <input className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none transition-all text-sm" placeholder="example.com" value={newClient.website} onChange={e => setNewClient({ ...newClient, website: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-black text-slate-500 uppercase tracking-widest mb-2 sm:mb-3">Client Notes</label>
                  <textarea className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none transition-all text-sm resize-none" rows="3" placeholder="Additional details..." value={newClient.notes} onChange={e => setNewClient({ ...newClient, notes: e.target.value })}></textarea>
                </div>
                <div>
                  <label className="block text-sm font-black text-slate-500 uppercase tracking-widest mb-2 sm:mb-3">Domain Information</label>
                  <textarea className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none transition-all text-sm resize-none" rows="3" placeholder="Registrar, DNS settings..." value={newClient.domain} onChange={e => setNewClient({ ...newClient, domain: e.target.value })}></textarea>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-black text-slate-500 uppercase tracking-widest mb-2 sm:mb-3">Service Tier</label>
                  <select className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none transition-all text-sm appearance-none" value={newClient.planId} onChange={e => setNewClient({ ...newClient, planId: e.target.value })} required>
                    <option value="">Select Plan</option>
                    {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-black text-slate-500 uppercase tracking-widest mb-2 sm:mb-3">Partner Agency</label>
                  <select className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none transition-all text-sm appearance-none" value={newClient.agencyId} onChange={e => setNewClient({ ...newClient, agencyId: e.target.value })}>
                    <option value="">Direct Account</option>
                    {agencies.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-black text-slate-500 uppercase tracking-widest mb-2 sm:mb-3">Reset Date</label>
                  <input type="date" className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none transition-all text-sm [color-scheme:dark]" value={newClient.resetDate} onChange={e => setNewClient({ ...newClient, resetDate: e.target.value })} required />
                </div>
              </div>

              {tags.length > 0 && (
                <div>
                  <label className="block text-sm font-black text-slate-500 uppercase tracking-widest mb-2 sm:mb-3">Tags & Labels</label>
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => {
                      const isSelected = newClient.tags.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            setNewClient(prev => ({
                              ...prev,
                              tags: isSelected
                                ? prev.tags.filter(t => t !== tag.id)
                                : [...prev.tags, tag.id]
                            }));
                          }}
                          className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-widest border transition-all duration-200 ${
                            isSelected
                              ? 'shadow-lg shadow-black/20'
                              : 'opacity-50 hover:opacity-80'
                          }`}
                          style={{
                            color: tag.color || '#94a3b8',
                            backgroundColor: isSelected ? `${tag.color || '#94a3b8'}20` : 'transparent',
                            borderColor: isSelected ? (tag.color || '#94a3b8') : `${tag.color || '#94a3b8'}40`
                          }}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2 text-slate-500 hover:text-white font-bold transition-colors text-sm uppercase">Cancel</button>
                <button type="submit" className="px-8 py-3 bg-teal-600 text-white rounded-xl font-black hover:bg-teal-500 shadow-lg transition-all text-sm uppercase">Confirm</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal isOpen={deleteConfirm.show} onClose={() => setDeleteConfirm({ show: false, id: null })} onConfirm={() => removeClient(deleteConfirm.id)} title="Offboard Client?" message="This will remove the client account and all associated task history." />
    </div>
  );
};

export default Dashboard;